import { useLocation, useNavigate } from 'react-router-dom'
import { CheckCircle, ExternalLink, ArrowLeft, Plus } from 'lucide-react'
import { TrelloCard } from '../lib/trello'

export default function Success() {
  const location = useLocation()
  const navigate = useNavigate()
  const { results, board, list, totalTasks } = location.state || {}

  if (!results) {
    navigate('/dashboard')
    return null
  }

  // Filter out errors and get successful cards
  const createdCards = results.filter((result: any) => !result.error)
  const errors = results.filter((result: any) => result.error)

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <CheckCircle className="w-12 h-12 text-green-500" />
        </div>
        <h1 className="text-3xl font-bold text-text mb-4">✅ Success!</h1>
        <p className="text-gray-600">
          {createdCards.length > 0 ? `Successfully created ${createdCards.length} cards in Trello` : 'Task creation completed'}
        </p>
      </div>

      <div className="card mb-8">
        <h2 className="text-xl font-semibold mb-4">📊 Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{createdCards.length}</div>
            <div className="text-sm text-green-700">Cards Created</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-lg font-semibold text-blue-600">{board || 'Multiple Boards'}</div>
            <div className="text-sm text-blue-700">Board</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-lg font-semibold text-purple-600">{list || 'Multiple Lists'}</div>
            <div className="text-sm text-purple-700">List</div>
          </div>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="card mb-8 border-red-200 bg-red-50">
          <h2 className="text-xl font-semibold mb-4 text-red-800">⚠️ Errors</h2>
          <div className="space-y-2">
            {errors.map((error: any, index: number) => (
              <div key={index} className="p-3 bg-red-100 rounded-lg">
                <p className="text-red-800 font-medium">{error.task}</p>
                <p className="text-red-600 text-sm">{error.error}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {createdCards.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">📋 Created Cards</h2>
          <div className="space-y-3">
            {createdCards.map((card: any, index: number) => (
              <div key={card.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{card.name}</h3>
                  {card.desc && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{card.desc}</p>
                  )}
                </div>
                {card.url && (
                  <a
                    href={card.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 ml-4"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="text-sm">View</span>
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center mt-8 space-y-4">
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-outline flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create More Tasks</span>
          </button>
        </div>
        
        <div className="mt-6">
          <a
            href={`https://trello.com/b/${board?.toLowerCase().replace(/\s+/g, '-')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700"
          >
            <ExternalLink className="w-4 h-4" />
            <span>View Board in Trello</span>
          </a>
        </div>
      </div>
    </div>
  )
} 